import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithTokenHandling } from '../baseQuery';
import { buildSearchParamsAuto } from '../utils';
import { PROVIDER_COHORTS_TAG_TYPE, ALL_COHORTS_ID } from './constants';
import {
  CreateProviderCohortData,
  UpdateProviderCohortData,
  CreateProviderCohortResponse,
  ProviderCohortsListResponse,
  ProviderCohort,
} from './types';

export const providerCohortsApi = createApi({
  reducerPath: 'providerCohortsApi',
  baseQuery: baseQueryWithTokenHandling,
  tagTypes: [PROVIDER_COHORTS_TAG_TYPE],
  endpoints: (builder) => ({
    getProviderCohorts: builder.query<
      ProviderCohortsListResponse,
      { page?: number; pageSize?: number } | void
    >({
      query: (options) => {
        const params = options
          ? buildSearchParamsAuto({
              page: options.page ? options.page + 1 : undefined,
              pageSize: options.pageSize,
            })
          : '';
        return `v1/search/provider-cohorts/${params ? '?' + params : ''}`;
      },
      providesTags: (result) => {
        const cohorts = result?.results || [];
        return [
          { type: PROVIDER_COHORTS_TAG_TYPE, id: ALL_COHORTS_ID },
          ...cohorts.map(({ id }: any) => ({
            type: PROVIDER_COHORTS_TAG_TYPE,
            id,
          })),
        ];
      },
    }),

    getProviderCohortById: builder.query<ProviderCohort, string>({
      query: (id) => `v1/search/provider-cohorts/${id}/`,
      providesTags: (result, error, id) => [
        { type: PROVIDER_COHORTS_TAG_TYPE, id },
      ],
    }),

    createProviderCohort: builder.mutation<
      CreateProviderCohortResponse,
      CreateProviderCohortData
    >({
      query: (data) => ({
        url: 'v1/search/provider-cohorts/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [
        { type: PROVIDER_COHORTS_TAG_TYPE, id: ALL_COHORTS_ID },
      ],
    }),

    updateProviderCohort: builder.mutation<
      ProviderCohort,
      { id: string; data: UpdateProviderCohortData }
    >({
      query: ({ id, data }) => ({
        url: `v1/search/provider-cohorts/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: PROVIDER_COHORTS_TAG_TYPE, id },
        { type: PROVIDER_COHORTS_TAG_TYPE, id: ALL_COHORTS_ID },
      ],
    }),

    deleteProviderCohort: builder.mutation<void, string>({
      query: (id) => ({
        url: `v1/search/provider-cohorts/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, id) => [
        { type: PROVIDER_COHORTS_TAG_TYPE, id },
        { type: PROVIDER_COHORTS_TAG_TYPE, id: ALL_COHORTS_ID },
      ],
    }),

    reactivateProviderCohort: builder.mutation<ProviderCohort, string>({
      query: (id) => ({
        url: `v1/search/provider-cohorts/${id}/reactivate/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: PROVIDER_COHORTS_TAG_TYPE, id },
        { type: PROVIDER_COHORTS_TAG_TYPE, id: ALL_COHORTS_ID },
      ],
    }),
  }),
});

export const {
  useGetProviderCohortsQuery,
  useGetProviderCohortByIdQuery,
  useCreateProviderCohortMutation,
  useUpdateProviderCohortMutation,
  useDeleteProviderCohortMutation,
  useReactivateProviderCohortMutation,
} = providerCohortsApi;
